/**
 * The redaction token — `[REDACTED]` — as the shell reads it (D145, the
 * descent arc §7).
 *
 * @remarks
 * Her clearance lines carry the literal token in the redacted variant; the
 * cleared variant is the same sentence with the span filled (Calder's craft
 * rule: a span removed from ONE sentence, everything outside the hole
 * character-identical). The token is TEXT all the way down — the transcript
 * stores it, the export prints it, the audit strips it (D136) — and only
 * here, at the last step before the eye, does it become a bar. That is the
 * ▷-sigil precedent (D85): one textual signature that survives every medium,
 * and on paper a redaction looks exactly like this.
 *
 * Framework-free, like the cadence table beside it, because the plain-DOM
 * 404 page steps text with the same arithmetic: a redaction is ONE reveal
 * step — a bar is put on a display in one motion, the way a surrogate pair
 * is one glyph — so the duration a turn is sequenced by (D102) and the steps
 * a renderer draws must agree, and they agree by reading one function.
 *
 * @packageDocumentation
 */

/** The literal token authored prose carries where a span has been withheld. */
export const REDACTION_TOKEN = '[REDACTED]';

/** One run of a line: prose as written, or a withheld span. */
export type RedactionSegment = { readonly kind: 'redaction' } | { readonly kind: 'text'; readonly text: string };

/** One unit of a stepped reveal: a code point, or a whole redaction. */
export type RevealStep = { readonly kind: 'character'; readonly text: string } | { readonly kind: 'redaction' };

/**
 * Split a line into its prose runs and its redactions, in order — empty
 * prose runs omitted, so adjacent tokens are adjacent bars.
 *
 * @param text - The line as the transcript stores it
 * @returns The line's segments (empty for an empty line)
 */
export const segmentRedactions = (text: string): readonly RedactionSegment[] =>
	text.split(REDACTION_TOKEN).flatMap<RedactionSegment>((run, runIndex) => [
		...(runIndex === 0 ? [] : [{ kind: 'redaction' } as const]),
		...(run === '' ? [] : [{ kind: 'text', text: run } as const]),
	]);

/**
 * A line as the stepped reveal walks it: one step per code point — never
 * per UTF-16 unit, so a surrogate pair is one glyph — and one step per
 * redaction.
 *
 * @param text - The line as the transcript stores it
 * @returns The steps in order
 */
export const revealStepsOf = (text: string): readonly RevealStep[] =>
	segmentRedactions(text).flatMap<RevealStep>((segment) =>
		segment.kind === 'redaction'
			? [segment]
			: Array.from(segment.text, (character) => ({ kind: 'character', text: character }) as const),
	);
